import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { COLORS } from 'muiTheme/colors';
import { useComments } from 'providers/CommentsContext';
import { ContentType } from 'utils/getContentType';
import { useAppSelector } from '../grid/reduxStore/Store';
import { selectContentType } from '../grid/reduxStore/documentSettingsSlice';
import { useSectionsDrawer } from '../SectionsDrawer/SectionsDrawerContext';
import { useEffectiveOrientation } from 'hooks/useEffectiveOrientation';
import { selectIsRightPanelCollapsed } from '../grid/reduxStore/blockStyleSettingsSlice';
import { useEffect } from 'react';

export const TOP_BAR_HEIGHT = 'var(--editor-header-height, 7.7rem)';

const SIDE_PANEL_Z_INDEX = 201; // because editor content's z-index is @z-index-level2 = 200

const RIGHT_PANEL_WIDTH = '358px';
const RIGHT_PANEL_WIDTH_WITH_AI = '520px';
const RIGHT_PANEL_WIDTH_WITH_COMMENTS = '5rem';
const RIGHT_PANEL_COLLAPSED_WIDTH = '5rem';
const LEFT_PANEL_WIDTH = '13.75rem';

export function DocumentBuilderRightPanel({ children, isAiTabActive }: { children: React.ReactNode; isAiTabActive: boolean }) {
  const { commentsEnabled } = useComments();
  const isRightPanelCollapsed = useAppSelector(selectIsRightPanelCollapsed);

  const width = commentsEnabled
    ? RIGHT_PANEL_WIDTH_WITH_COMMENTS
    : isRightPanelCollapsed
    ? RIGHT_PANEL_COLLAPSED_WIDTH
    : isAiTabActive
    ? RIGHT_PANEL_WIDTH_WITH_AI
    : RIGHT_PANEL_WIDTH;

  return (
    <Drawer
      open
      className="editor-right-sider"
      sx={{
        width,
        flexShrink: 0,
        transition: 'width 350ms ease-out',
        '& .MuiDrawer-paper': {
          overflow: 'hidden',
          boxSizing: 'border-box',
          backgroundColor: 'transparent',
          marginTop: TOP_BAR_HEIGHT,
          height: `calc(100vh - ${TOP_BAR_HEIGHT})`,
          zIndex: SIDE_PANEL_Z_INDEX,
          borderLeft: 'none',
          width,
          transition: 'width 350ms ease-out, z-index 350ms ease-out',
        },
      }}
      variant="persistent"
      anchor="right"
    >
      {children}
    </Drawer>
  );
}

export function DocumentBuilderLeftPanel({ children }: { children: React.ReactNode }) {
  const { isSectionsDrawerOpen } = useSectionsDrawer();

  // note: do NOT return null for the open/close state. it will break the animation.

  return (
    <Drawer
      className="editor-left-sider"
      open={isSectionsDrawerOpen} // this is the way for the animation to work.
      sx={{
        width: LEFT_PANEL_WIDTH,
        flexShrink: 0,

        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          backgroundColor: COLORS.appFog100,
          marginTop: TOP_BAR_HEIGHT,
          zIndex: SIDE_PANEL_Z_INDEX,
          borderLeft: 'none',
          height: `calc(100vh - ${TOP_BAR_HEIGHT})`,
          marginLeft: 'var(--sidebar-width, 0)',
          width: LEFT_PANEL_WIDTH,
          boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.10) inset',
          overflowX: 'hidden',
          overflowY: 'auto',
          '@media (max-width: 998px)': {
            marginLeft: 0,
          },
        },
      }}
      variant="persistent"
      anchor="left"
    >
      {children}
    </Drawer>
  );
}

export function DocumentBuilderMainContent({ children }: { children: React.ReactNode }) {
  const { isSectionsDrawerOpen, setIsSectionsDrawerOpen } = useSectionsDrawer();
  const { commentsEnabled } = useComments();
  const isRightPanelCollapsed = useAppSelector(selectIsRightPanelCollapsed);
  const orientation = useEffectiveOrientation();

  useEffect(() => {
    if (commentsEnabled) setIsSectionsDrawerOpen(false);
  }, [commentsEnabled]);

  return (
    <Box
      data-testid="main-content-wrapper"
      sx={(theme) => ({
        paddingTop: TOP_BAR_HEIGHT,
        paddingBottom: '140px',
        marginLeft: isSectionsDrawerOpen ? LEFT_PANEL_WIDTH : 0,
        marginRight: commentsEnabled ? 0 : isRightPanelCollapsed ? RIGHT_PANEL_COLLAPSED_WIDTH : RIGHT_PANEL_WIDTH,
        transition: 'margin-right 350ms ease-out',
        ...(orientation === 'landscape' && {
          overflowX: 'auto',
          overflowY: 'auto',
          height: '100vh',
          backgroundColor: theme.palette.background.default,
        }),
        ...(commentsEnabled && {
          width: '100%',
          marginRight: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '40px',
          position: 'relative',
        }),
      })}
    >
      {children}
    </Box>
  );
}

export function DocumentBuilderEditorMainContent({ children }: { children: React.ReactNode }) {
  const contentType = useAppSelector(selectContentType);

  return (
    <Box
      className={`editor-document-builder ${
        contentType === ContentType.TEMPLATE || contentType === ContentType.SECTION ? 'editor-library-asset-builder' : ''
      }`}
      sx={{
        transition: 'margin-left 0.3s ease',
        position: 'relative',
      }}
    >
      {children}
    </Box>
  );
}
